/**
 * Hook that manages the native MapLibre navigation overlay.
 * Automatically shows the native map when route following starts,
 * sends position updates for smooth camera tracking, and hides
 * it when navigation ends.
 *
 * Only active on Android native — no-op on web/iOS.
 */
import { useEffect, useRef } from 'react';
import type { CampingSpot, Coordinates, RouteFollowState } from '../types';
import {
  isNativeNavMapAvailable,
  showNativeNavMap,
  hideNativeNavMap,
  updateNativeNavPosition,
  setNativeNavRoute,
  clearNativeNavRoute,
  setNativeNavStyle,
  setNativeNavNextSpot,
  setNativeNavInfoText,
  destroyNativeNavMap,
} from '../services/nativeNavMapService';
import { getDistance, getPolylineBbox, isPointNearPolyline } from '../services/geometryUtils';

interface UseNativeNavMapOptions {
  followRouteState: RouteFollowState | null;
  userPosition: Coordinates | null;
  userHeading: number | null;
  mapStyle: 'topo' | 'satellite';
  isFollowing: boolean;
  spots?: CampingSpot[];
  /** Meters traveled along the route (from routeFollowResult.progressM). */
  progressM?: number | null;
}

// Throttle position updates to max ~15fps for smooth but efficient animation
const POSITION_THROTTLE_MS = 66;

export function useNativeNavMap({
  followRouteState,
  userPosition,
  userHeading,
  mapStyle,
  isFollowing,
  spots,
  progressM,
}: UseNativeNavMapOptions): boolean {
  const isActive = useRef(false);
  const lastUpdateRef = useRef(0);
  const routeIdRef = useRef<string | null>(null);
  const lastNextSpotRef = useRef<{ id: string; lat: number; lon: number; name: string } | null>(null);

  // Show/hide native map based on follow state
  useEffect(() => {
    if (!isNativeNavMapAvailable()) return;

    if (isFollowing && userPosition && followRouteState) {
      // Start native map
      if (!isActive.current) {
        isActive.current = true;
        // Reset next-spot memo so the compute effect re-pushes the marker
        // to a fresh native session (without this, when AA disconnects and
        // the phone takes over, the green pivot pin is missing because
        // lastNextSpotRef matches the previous AA session's spot id).
        lastNextSpotRef.current = null;
        showNativeNavMap({
          style: mapStyle,
          latitude: userPosition.latitude,
          longitude: userPosition.longitude,
          bearing: userHeading ?? 0,
        });
      }

      // Send route polyline if new route
      if (followRouteState.routeId !== routeIdRef.current) {
        routeIdRef.current = followRouteState.routeId;
        const coords: [number, number][] = followRouteState.routeCoords.map(
          c => [c.longitude, c.latitude]
        );
        setNativeNavRoute(coords);
      }
    } else {
      // Stop native map
      if (isActive.current) {
        isActive.current = false;
        routeIdRef.current = null;
        clearNativeNavRoute();
        hideNativeNavMap();
      }
    }
  }, [isFollowing, followRouteState?.routeId]);

  // Send position updates while active (throttled)
  useEffect(() => {
    if (!isActive.current || !userPosition) return;

    const now = Date.now();
    if (now - lastUpdateRef.current < POSITION_THROTTLE_MS) return;
    lastUpdateRef.current = now;

    updateNativeNavPosition(
      userPosition.latitude,
      userPosition.longitude,
      userHeading ?? 0,
      15,
      true,
    );
  }, [userPosition?.latitude, userPosition?.longitude, userHeading]);

  // Sync map style while active
  useEffect(() => {
    if (!isActive.current) return;
    setNativeNavStyle(mapStyle);
  }, [mapStyle]);

  // Compute "next Fricamp along the route ahead of the user" and push to the
  // native overlay so the driver sees a green pin for the next pivot option.
  // Same algorithm as the AA-side compute: corridor within 2km + ahead of user.
  // Sticky: keep showing previous if no new candidate is found.
  // (lastNextSpotRef is declared at the top so the show/hide effect can reset it)
  useEffect(() => {
    if (!isActive.current) return;
    if (!followRouteState?.routeCoords || followRouteState.routeCoords.length < 2) return;
    if (!userPosition || !spots || spots.length === 0) return;

    const coords = followRouteState.routeCoords;
    const lastCoord = coords[coords.length - 1];
    const userToDest = getDistance(
      userPosition.latitude, userPosition.longitude,
      lastCoord.latitude, lastCoord.longitude,
    );
    const bbox = getPolylineBbox(coords);

    let best: { spot: CampingSpot; userDist: number } | null = null;
    for (const s of spots) {
      if (!s.location || s.isDeleted || s.isInvalid) continue;
      // Skip the destination spot itself
      const distToDest = getDistance(
        s.location.latitude, s.location.longitude,
        lastCoord.latitude, lastCoord.longitude,
      );
      if (distToDest < 100) continue;
      // Must be ahead of the user (closer to destination)
      if (distToDest >= userToDest - 50) continue;
      // Must be within route corridor
      if (!isPointNearPolyline(s.location, coords, 2000, bbox)) continue;
      const userDist = getDistance(
        userPosition.latitude, userPosition.longitude,
        s.location.latitude, s.location.longitude,
      );
      if (!best || userDist < best.userDist) best = { spot: s, userDist };
    }

    if (best) {
      const next = {
        id: best.spot.id,
        lat: best.spot.location.latitude,
        lon: best.spot.location.longitude,
        name: best.spot.name || '',
      };
      if (lastNextSpotRef.current?.id !== next.id) {
        lastNextSpotRef.current = next;
        setNativeNavNextSpot({ latitude: next.lat, longitude: next.lon, name: next.name });
      }
    }
    // If no candidate, keep the last marker — better than flickering it off
  }, [userPosition?.latitude, userPosition?.longitude, followRouteState?.routeId, spots]);

  // Clear next-spot marker when navigation stops
  useEffect(() => {
    if (!isFollowing && lastNextSpotRef.current !== null) {
      lastNextSpotRef.current = null;
      setNativeNavNextSpot(null);
    }
  }, [isFollowing]);

  // Info overlay text: remaining distance + ETA. Shown at top of native map
  // so the driver knows how far is left without falling back to AA's display.
  useEffect(() => {
    if (!isActive.current) return;
    if (!followRouteState) {
      setNativeNavInfoText('');
      return;
    }
    const total = followRouteState.routeTotalDistanceM;
    const totalDur = followRouteState.routeTotalDurationS ?? (total / (50 / 3.6));
    const progressed = progressM ?? 0;
    const remaining = Math.max(0, total - progressed);
    const remainingDur = total > 0 ? (totalDur * remaining) / total : 0;
    const distText = remaining < 1000
      ? `${Math.round(remaining)} m`
      : `${(remaining / 1000).toFixed(1)} km`;
    const durText = remainingDur < 60
      ? `${Math.round(remainingDur)} s`
      : remainingDur < 3600
        ? `${Math.round(remainingDur / 60)} min`
        : `${Math.floor(remainingDur / 3600)} t ${Math.round((remainingDur % 3600) / 60)} min`;
    setNativeNavInfoText(`${distText} igjen · ${durText}`);
  }, [progressM, followRouteState?.routeId, followRouteState?.routeTotalDistanceM]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isActive.current) {
        isActive.current = false;
        destroyNativeNavMap();
      }
    };
  }, []);

  return isActive.current;
}
